if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this._touchStartX = null;
        this._touchStartY = null;
        this._touchStartTime = null;
        this._touchLastX = null;
        this._touchLastY = null;
        this._touchStartScrollTop = 0;
        this._isSwiping = false;
        this._isClosingSwipe = false;
        this._animatingSwipe = false;
        this._boundOnResize = this.onResize.bind(this);
        this._boundOnTouchStart = this.onTouchStart.bind(this);
        this._boundOnTouchMove = this.onTouchMove.bind(this);
        this._boundOnTouchEnd = this.onTouchEnd.bind(this);
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();

        this.bindNavButtons();
        this.updateNavVisibility();
        this.updateNavPosition();
        window.addEventListener('resize', this._boundOnResize);

        if (window.matchMedia('(max-width: 749px)').matches) {
          this.enableSwipeNavigation();
        } else {
          this.disableSwipeNavigation();
        }
      }

      hide() {
        this.disableSwipeNavigation();
        const content = this.querySelector('.product-media-modal__content');
        if (content) content.classList.remove('is-loading');
        this._activeLoadingImage = null;
        this.style.removeProperty('--product-media-modal-nav-top');
        window.removeEventListener('resize', this._boundOnResize);
        super.hide();
      }

      onResize() {
        this.updateNavVisibility();
        this.updateNavPosition();
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        if (!mediaId) return;
        this.setActiveMediaById(mediaId);
      }

      getMediaElements() {
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return [];
        return Array.from(content.children).filter((el) => el && el.hasAttribute('data-media-id'));
      }

      getActiveMediaElement() {
        return this.getMediaElements().find((el) => el.classList.contains('active')) || null;
      }

      setActiveMediaById(mediaId) {
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return;

        const mediaElements = this.getMediaElements();
        mediaElements.forEach((el) => {
          if (el.getAttribute('data-media-id') === String(mediaId)) el.classList.add('active');
          else el.classList.remove('active');
        });

        const activeMedia = mediaElements.find((el) => el.getAttribute('data-media-id') === String(mediaId));
        if (!activeMedia) return;

        this.applySkeletonUntilLoaded(activeMedia);
        this.updateNavVisibility();
        this.updateNavPosition();

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      updateNavPosition() {
        const prevButton = this.querySelector('.product-media-modal__nav--prev');
        const nextButton = this.querySelector('.product-media-modal__nav--next');
        if (!prevButton && !nextButton) return;

        if (!window.matchMedia('(max-width: 749px)').matches) {
          this.style.removeProperty('--product-media-modal-nav-top');
          return;
        }

        window.requestAnimationFrame(() => {
          const active = this.getActiveMediaElement();
          if (!active) {
            this.style.removeProperty('--product-media-modal-nav-top');
            return;
          }

          const target = active.nodeName === 'IMG' ? active : active.querySelector?.('img') || active;
          if (!target || !target.getBoundingClientRect) return;

          const rect = target.getBoundingClientRect();
          if (!rect.height) return;

          const centerY = rect.top + rect.height / 2;
          const padding = 56;
          const clamped = Math.min(Math.max(centerY, padding), window.innerHeight - padding);
          this.style.setProperty('--product-media-modal-nav-top', `${Math.round(clamped)}px`);
        });
      }

      bindNavButtons() {
        if (this._navButtonsBound) return;
        this._navButtonsBound = true;

        const prevButton = this.querySelector('.product-media-modal__nav--prev');
        const nextButton = this.querySelector('.product-media-modal__nav--next');

        if (prevButton) {
          prevButton.addEventListener('click', (event) => {
            event.preventDefault();
            this.animateSwipeTo('prev');
          });
        }

        if (nextButton) {
          nextButton.addEventListener('click', (event) => {
            event.preventDefault();
            this.animateSwipeTo('next');
          });
        }
      }

      updateNavVisibility() {
        const prevButton = this.querySelector('.product-media-modal__nav--prev');
        const nextButton = this.querySelector('.product-media-modal__nav--next');
        if (!prevButton && !nextButton) return;

        const mediaCount = this.getMediaElements().length;
        const shouldShow = mediaCount > 1 && window.matchMedia('(max-width: 749px)').matches;

        if (prevButton) prevButton.toggleAttribute('hidden', !shouldShow);
        if (nextButton) nextButton.toggleAttribute('hidden', !shouldShow);
      }

      applySkeletonUntilLoaded(activeMediaElement) {
        const content = this.querySelector('.product-media-modal__content');
        const img =
          activeMediaElement?.nodeName === 'IMG'
            ? activeMediaElement
            : activeMediaElement?.querySelector?.('img.global-media-settings, img');

        if (!img) return;

        this._activeLoadingToken = (this._activeLoadingToken || 0) + 1;
        const token = this._activeLoadingToken;
        this._activeLoadingImage = img;

        // If already loaded (cache), don't show skeleton.
        if (img.complete && img.naturalWidth > 0) {
          img.classList.remove('is-loading');
          if (content) content.classList.remove('is-loading');
          return;
        }

        img.classList.add('is-loading');
        if (content) content.classList.add('is-loading');

        const onDone = () => {
          img.classList.remove('is-loading');
          if (content && this._activeLoadingToken === token && this._activeLoadingImage === img) {
            content.classList.remove('is-loading');
          }
        };

        img.addEventListener('load', onDone, { once: true });
        img.addEventListener('error', onDone, { once: true });
      }

      enableSwipeNavigation() {
        if (this._swipeEnabled) return;
        this._swipeEnabled = true;
        this.addEventListener('touchstart', this._boundOnTouchStart, { passive: true });
        this.addEventListener('touchmove', this._boundOnTouchMove, { passive: false });
        this.addEventListener('touchend', this._boundOnTouchEnd, { passive: true });
      }

      disableSwipeNavigation() {
        if (!this._swipeEnabled) return;
        this._swipeEnabled = false;
        this.removeEventListener('touchstart', this._boundOnTouchStart);
        this.removeEventListener('touchmove', this._boundOnTouchMove);
        this.removeEventListener('touchend', this._boundOnTouchEnd);
        this.resetSwipeState();
      }

      resetSwipeState() {
        this._touchStartX = null;
        this._touchStartY = null;
        this._touchStartTime = null;
        this._touchLastX = null;
        this._touchLastY = null;
        this._touchStartScrollTop = 0;
        this._isSwiping = false;
        this._isClosingSwipe = false;

        const active = this.getActiveMediaElement();
        if (active) {
          active.style.transform = '';
          active.style.opacity = '';
          active.style.transition = '';
          active.style.willChange = '';
        }
      }

      onTouchStart(event) {
        if (!event.touches || event.touches.length !== 1) return;
        if (this._animatingSwipe) return;

        const target = event.target;
        if (target && target.closest && target.closest('product-model, model-viewer, iframe, video')) return;
        if (target && target.closest && target.closest('.product-media-modal__nav, .product-media-modal__toggle')) return;

        this._touchStartX = event.touches[0].clientX;
        this._touchStartY = event.touches[0].clientY;
        this._touchLastX = this._touchStartX;
        this._touchLastY = this._touchStartY;
        this._touchStartTime = Date.now();
        this._isSwiping = false;
        this._isClosingSwipe = false;

        const container = this.querySelector('[role="document"]');
        this._touchStartScrollTop = container ? container.scrollTop : 0;

        const active = this.getActiveMediaElement();
        if (active) {
          active.style.transition = 'none';
          active.style.willChange = 'transform';
        }
      }

      onTouchMove(event) {
        if (this._touchStartX == null || this._touchStartY == null) return;
        if (!event.touches || event.touches.length !== 1) return;
        if (this._animatingSwipe) return;

        const currentX = event.touches[0].clientX;
        const currentY = event.touches[0].clientY;
        const dx = currentX - this._touchStartX;
        const dy = currentY - this._touchStartY;
        this._touchLastX = currentX;
        this._touchLastY = currentY;

        const distance = Math.hypot(dx, dy);
        const directionLockDistance = 18;
        const horizontalDominance = 1.25;
        const verticalDominance = 1.5;

        const container = this.querySelector('[role="document"]');
        const isAtTop = (container?.scrollTop || 0) <= 0 && this._touchStartScrollTop <= 0;

        // Decide gesture direction only after the finger moved a bit.
        if (!this._isSwiping && !this._isClosingSwipe) {
          if (distance < directionLockDistance) return;

          const absDx = Math.abs(dx);
          const absDy = Math.abs(dy);

          // Prefer horizontal swipe when horizontal movement dominates.
          if (absDx > absDy * horizontalDominance) {
            this._isSwiping = true;
          } else if (dy > 0 && isAtTop && absDy > absDx * verticalDominance) {
            // Enable swipe-down to close only when clearly vertical and starting at the top.
            this._isClosingSwipe = true;
          } else {
            return;
          }
        }

        const active = this.getActiveMediaElement();
        if (!active) return;

        if (this._isClosingSwipe) {
          event.preventDefault();
          const translateY = Math.max(0, dy);
          active.style.transform = `translateY(${translateY}px)`;
          const fade = Math.min(translateY / 500, 0.35);
          active.style.opacity = String(1 - fade);
          return;
        }

        // Prevent the modal content from vertical scrolling while swiping horizontally.
        event.preventDefault();

        active.style.transform = `translateX(${dx}px)`;
        const fade = Math.min(Math.abs(dx) / 500, 0.2);
        active.style.opacity = String(1 - fade);
      }

      onTouchEnd(event) {
        if (this._touchStartX == null || this._touchStartY == null) return;
        if (!event.changedTouches || event.changedTouches.length !== 1) return;
        if (this._animatingSwipe) return;

        const endX = event.changedTouches[0].clientX;
        const endY = event.changedTouches[0].clientY;
        const dx = endX - this._touchStartX;
        const dy = endY - this._touchStartY;
        const dt = Date.now() - (this._touchStartTime || Date.now());

        const wasSwiping = this._isSwiping;
        const wasClosingSwipe = this._isClosingSwipe;
        this._touchStartX = null;
        this._touchStartY = null;
        this._touchStartTime = null;
        this._touchLastX = null;
        this._touchLastY = null;
        this._touchStartScrollTop = 0;
        this._isSwiping = false;
        this._isClosingSwipe = false;

        const minDistance = 50;
        const maxTime = 600;

        // If we didn't enter a gesture mode, don't interfere.
        if (!wasSwiping && !wasClosingSwipe) return;

        if (wasClosingSwipe) {
          const active = this.getActiveMediaElement();
          if (!active) return;

          const minCloseDistance = 120;
          const closeMaxTime = 700;
          const closeVerticalDominance = 1.35;

          if (dt <= closeMaxTime && dy > minCloseDistance && Math.abs(dy) > Math.abs(dx) * closeVerticalDominance) {
            this.animateCloseDown(active);
          } else {
            this.animateSnapBackVertical(active);
          }
          return;
        }

        // If it was a short swipe, animate back to center.
        if (dt > maxTime || Math.abs(dx) < minDistance || Math.abs(dy) > Math.abs(dx) * 0.75) {
          this.animateSnapBack();
          return;
        }

        if (dx < 0) this.animateSwipeTo('next');
        else this.animateSwipeTo('prev');
      }

      animateSnapBackVertical(active) {
        this._animatingSwipe = true;
        active.style.transition = 'transform 180ms ease, opacity 180ms ease';
        active.style.transform = 'translateY(0)';
        active.style.opacity = '1';
        window.setTimeout(() => {
          this._animatingSwipe = false;
          active.style.transition = '';
          active.style.transform = '';
          active.style.opacity = '';
          active.style.willChange = '';
        }, 200);
      }

      animateCloseDown(active) {
        this._animatingSwipe = true;
        active.style.transition = 'transform 200ms ease, opacity 200ms ease';
        active.style.transform = 'translateY(120px)';
        active.style.opacity = '0';
        window.setTimeout(() => {
          this._animatingSwipe = false;
          active.style.transition = '';
          active.style.transform = '';
          active.style.opacity = '';
          active.style.willChange = '';
          this.hide();
        }, 210);
      }

      animateSnapBack() {
        const active = this.getActiveMediaElement();
        if (!active) return;
        this._animatingSwipe = true;
        active.style.transition = 'transform 180ms ease, opacity 180ms ease';
        active.style.transform = 'translateX(0)';
        active.style.opacity = '1';
        window.setTimeout(() => {
          this._animatingSwipe = false;
          active.style.transition = '';
          active.style.transform = '';
          active.style.opacity = '';
          active.style.willChange = '';
        }, 200);
      }

      animateSwipeTo(direction) {
        if (this._animatingSwipe) return;
        const mediaElements = this.getMediaElements();
        if (mediaElements.length < 2) {
          this.animateSnapBack();
          return;
        }

        const container = this.querySelector('[role="document"]');
        if (container) container.scrollTop = 0;

        const current = this.getActiveMediaElement();
        if (!current) return;

        const currentIndex = mediaElements.indexOf(current);
        if (currentIndex === -1) return;

        const nextIndex =
          direction === 'next'
            ? (currentIndex + 1) % mediaElements.length
            : (currentIndex - 1 + mediaElements.length) % mediaElements.length;

        const target = mediaElements[nextIndex];
        if (!target) return;

        this.applySkeletonUntilLoaded(target);

        this._animatingSwipe = true;

        // Keep both elements visible during the transition.
        current.style.display = 'block';
        target.style.display = 'block';

        // Prepare target off-screen.
        target.classList.add('active');
        current.classList.remove('active');

        const incomingFrom = direction === 'next' ? '100%' : '-100%';
        const outgoingTo = direction === 'next' ? '-100%' : '100%';

        target.style.transition = 'none';
        target.style.transform = `translateX(${incomingFrom})`;
        target.style.opacity = '1';

        // Animate.
        window.requestAnimationFrame(() => {
          current.style.transition = 'transform 220ms ease, opacity 220ms ease';
          current.style.transform = `translateX(${outgoingTo})`;
          current.style.opacity = '0';

          target.style.transition = 'transform 220ms ease';
          target.style.transform = 'translateX(0)';
        });

        window.setTimeout(() => {
          // Cleanup styles; non-active elements will be hidden by CSS.
          current.style.display = '';
          current.style.transition = '';
          current.style.transform = '';
          current.style.opacity = '';
          current.style.willChange = '';

          target.style.display = '';
          target.style.transition = '';
          target.style.transform = '';
          target.style.opacity = '';

          // Ensure only target remains active.
          mediaElements.forEach((el) => {
            if (el === target) el.classList.add('active');
            else el.classList.remove('active');
          });

          this._animatingSwipe = false;
          this.updateNavPosition();
        }, 260);
      }

      goToNextMedia() {
        const mediaElements = this.getMediaElements();
        if (mediaElements.length < 2) return;
        const active = this.getActiveMediaElement();
        const currentIndex = active ? mediaElements.indexOf(active) : -1;
        const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % mediaElements.length;
        this.setActiveMediaById(mediaElements[nextIndex].getAttribute('data-media-id'));
      }

      goToPreviousMedia() {
        const mediaElements = this.getMediaElements();
        if (mediaElements.length < 2) return;
        const active = this.getActiveMediaElement();
        const currentIndex = active ? mediaElements.indexOf(active) : -1;
        const prevIndex =
          currentIndex === -1 ? mediaElements.length - 1 : (currentIndex - 1 + mediaElements.length) % mediaElements.length;
        this.setActiveMediaById(mediaElements[prevIndex].getAttribute('data-media-id'));
      }
    }
  );
}
